import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

interface TimezonePickerProps {
  /** Merged onto the trigger button, so a caller can drop this into a
   *  differently-styled shell (e.g. the auth pages' native-input look)
   *  without forking the component. Later classes win via `cn`'s
   *  `tailwind-merge`, so this can override the default outline-button
   *  sizing and colours. */
  className?: string;
  disabled?: boolean;
  id?: string;
  onChange: (timeZone: string) => void;
  /** IANA zone, or null when the organization has not set one. */
  value: string | null;
}

/**
 * Common clinic zones, used when `Intl.supportedValuesOf` is unavailable.
 * Deliberately short — the full IANA list comes from the browser when it can.
 */
const FALLBACK_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'UTC',
];

const allZones = (): string[] => {
  const supported = Intl.supportedValuesOf;
  if (typeof supported !== 'function') return FALLBACK_ZONES;
  try {
    const zones = supported('timeZone') as string[];
    // Some ICU builds omit 'UTC' from the live list even though the backend
    // accepts it — guarantee it's always selectable rather than merging in
    // the whole curated fallback list.
    return zones.includes('UTC') ? zones : ['UTC', ...zones];
  } catch {
    return FALLBACK_ZONES;
  }
};

export function TimezonePicker({ id, value, onChange, className, disabled = false }: TimezonePickerProps) {
  const [open, setOpen] = useState(false);
  const zones = useMemo(allZones, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          role="combobox"
          variant="outline"
          disabled={disabled}
          aria-expanded={open}
          className={cn('w-full justify-between font-normal', !value && 'text-muted-foreground', className)}>
          <span className="flex min-w-0 items-center gap-2">
            <Globe className="h-4 w-4 shrink-0" />
            <span className="truncate">{value ?? 'Select a timezone'}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0">
        <Command>
          <CommandInput placeholder="Search timezones…" />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            <CommandGroup>
              {zones.map((zone) => (
                <CommandItem
                  key={zone}
                  value={zone}
                  onSelect={() => {
                    onChange(zone);
                    setOpen(false);
                  }}>
                  <Check className={cn('mr-2 h-4 w-4', zone === value ? 'opacity-100' : 'opacity-0')} />
                  {zone}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
